<template>
	<div class="grid">
		<div class="col-12">
			<div class="card">
				<Toast/>
				<DataTable ref="dt" :value="products" v-model:selection="selectedProducts" selectionMode="multiple" dataKey="id" :paginator="true" :rows="20" :filters="filters"
							paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" :rowsPerPageOptions="[20, 35, 50]"
							currentPageReportTemplate="Mostrando de {first} a {last} de {totalRecords} producto(s)" responsiveLayout="scroll">
					<template #header>
						<div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
							<h5 class="m-0">Administre productos</h5>
							<div class="table-header-inputs-container">
								<span id="table-search-input" class="block mt-2 md:mt-0 p-input-icon-left">
									<i class="pi pi-search" />
									<AutoComplete placeholder="Buscar por nombre..." ref="autocomplete" :dropdown="false" :multiple="false" v-model="filters['global'].value" @complete="onFilterChanged($event)"></AutoComplete>
								</span>
								<span class="table-buttons-container block mt-2 md:mt-0">
									<Button icon="pi pi-plus" v-show="this.isAuthorized" class="p-button-rounded p-button-success mr-2" @click="openNew" />
									<Button icon="pi pi-trash" v-show="this.isAuthorized" class="p-button-rounded p-button-danger mr-2" @click="confirmDeleteSelected" :disabled="!selectedProducts || !selectedProducts.length" />
									<Button icon="pi pi-download" v-show="this.isAuthorized" class="p-button-rounded p-button-help mr-2" :loading="loadingDownloadButton" @click="downloadProducts" />
								</span>
							</div>
						</div>
					</template>

                    <Column field="code" header="Código" :sortable="true" headerStyle="width:10%;">
						<template #body="slotProps">
							<span class="p-column-title">Código</span>
							{{slotProps.data.code}}
						</template>
					</Column>
					<Column field="name" header="Nombre" :sortable="true" headerStyle="width:40%;">
						<template #body="slotProps">
							<span class="p-column-title">Nombre</span>
							{{slotProps.data.name}}
							<span v-if="!slotProps.data.isSellable" class="supply-flag" :title="slotProps.data.stockMode === 'PERIODIC' ? 'Insumo de conteo periódico: no se vende' : 'Insumo: no se vende'">{{ slotProps.data.stockMode === 'PERIODIC' ? 'Insumo · conteo' : 'Insumo' }}</span>
						</template>
					</Column>
					<Column field="price" header="Precio unitario" :sortable="true" headerStyle="width:10%;">
						<template #body="slotProps">
							<span class="p-column-title">Precio</span>
							{{slotProps.data.price}}
						</template>
					</Column>
					<Column field="ivaCode" header="IVA" :sortable="true" headerStyle="width:10%;">
						<template #body="slotProps">
							<span class="p-column-title">IVA</span>
							<span :class="['iva-cell', { 'iva-cell--zero': !slotProps.data.hasTax }]">{{ ivaLabel(slotProps.data) }}</span>
							<span v-if="slotProps.data.hasIce" class="ice-flag" title="Aplica ICE">ICE</span>
						</template>
					</Column>
					<Column field="stock" header="Stock" :sortable="true" headerStyle="width:10%;">
						<template #body="slotProps">
							<span class="p-column-title">Stock</span>
							{{ (slotProps.data.stock !== undefined && slotProps.data.stock !== null && slotProps.data.hasStock) ? slotProps.data.stock : "-" }}
						</template>
					</Column>
                    <Column field="discount" header="Descuento" :sortable="true" headerStyle="width:5%;">
						<template #body="slotProps">
							<span class="p-column-title">Descuento</span>
							{{ formatPercent(slotProps.data.discount) }}
						</template>
					</Column>
                    <Column headerStyle="min-width:12rem; width:15%;" header="Acciones" v-if="this.isAuthorized">
						<template #body="slotProps">
							<div class="row-actions">
								<Button icon="pi pi-pencil" class="p-button-rounded p-button-success" @click="editProduct(slotProps.data)" />
								<Button icon="pi pi-list" v-if="slotProps.data.hasStock || slotProps.data.hasComposition" class="p-button-rounded p-button-info" @click="openInventoryDialog(slotProps.data)" />
								<Button icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmDeleteProduct(slotProps.data)" />
							</div>
						</template>
					</Column>
				</DataTable>

				<Dialog v-model:visible="productDialog" :style="{width: '520px'}" :contentStyle="{maxHeight: '70vh', overflowY: 'auto'}" header="Producto" :modal="true" class="p-fluid product-dialog">
                    <div class="field">
						<label for="code">Código (Opcional)</label>
                        <InputText id="code" v-model.trim="product.code" required="true" autofocus :class="{'p-invalid': submitted && !product.code}" />
                    </div>
					<div class="field">
						<label for="nombre">Nombre</label>
						<InputText id="nombre" v-model.trim="product.name" required="true" autofocus :class="{'p-invalid': submitted && !product.name}" />
						<small class="p-invalid" v-if="submitted && !product.name">El nombre es obligatorio.</small>
					</div>
					<div class="formgrid grid">
						<div class="field col-4">
							<label for="price">Precio 1</label>
							<InputText id="price" type="number" v-model.trim="product.price" required="true" autofocus :class="{'p-invalid': submitted && !product.price}" />
						</div>
						<div class="field col-4">
							<label for="price2">Precio 2</label>
							<InputText id="price2" type="number" v-model="product.price2" autofocus/>
						</div>
						<div class="field col-4">
							<label for="price3">Precio 3</label>
							<InputText id="price3" type="number" v-model.trim="product.price3" autofocus/>
						</div>
						<div class="field col-4">
							<label for="price4">Precio 4</label>
							<InputText id="price4" type="number" v-model.trim="product.price4" autofocus/>
						</div>
						<div class="field col-4">
							<label for="price5">Precio 5</label>
							<InputText id="price5" type="number" v-model.trim="product.price5" autofocus/>
						</div>
					</div>
					<div class="field">
						<label for="discount">Descuento (%)</label>
						<InputNumber id="discount" v-model="discountPercent" mode="decimal" :min="0" :max="99.99"
									:minFractionDigits="0" :maxFractionDigits="2" :useGrouping="false"
									locale="en-US" suffix=" %" />
					</div>
					<Divider align="left" class="section-divider">
						<span class="section-divider__label">Impuestos</span>
					</Divider>

					<div class="field">
						<label for="ivaCode">Tarifa de IVA</label>
						<SelectButton id="ivaCode" v-model="product.ivaCode" :options="ivaOptions"
									optionLabel="label" optionValue="value" :unselectable="false"
									class="iva-select" aria-labelledby="ivaCode" />
						<small class="field-hint">{{ ivaHint }}</small>
					</div>

					<div class="field toggle-row">
						<div class="toggle-row__text">
							<label for="hasIce">Aplica ICE</label>
							<small>Impuesto a Consumos Especiales. Solo fabricantes, importadores y servicios gravados.</small>
						</div>
						<InputSwitch id="hasIce" v-model="product.hasIce" />
					</div>

					<div v-if="product.hasIce" class="ice-panel">
						<div class="formgrid grid">
							<div class="field col-6">
								<label for="iceCode">Código de tarifa</label>
								<InputText id="iceCode" v-model.trim="product.iceCode" placeholder="Ej. 3051" />
							</div>
							<div class="field col-6">
								<label for="iceType">Modalidad</label>
								<Dropdown id="iceType" v-model="product.iceType" :options="iceTypes"
										optionLabel="label" optionValue="value" placeholder="Seleccionar" />
							</div>
							<div class="field col-6" v-if="showIceAdValorem">
								<label for="iceRate">Ad valorem (%)</label>
								<InputText id="iceRate" type="number" v-model.trim="product.iceRate" />
							</div>
							<div class="field col-6" v-if="showIceSpecific">
								<label for="iceSpecificRate">Específica (por unidad)</label>
								<InputText id="iceSpecificRate" type="number" v-model.trim="product.iceSpecificRate" />
							</div>
						</div>
					</div>

					<Divider align="left" class="section-divider">
						<span class="section-divider__label">Inventario</span>
					</Divider>

					<div class="field toggle-row">
						<div class="toggle-row__text">
							<label for="hasStock">Habilitar Inventario</label>
							<small>Descuenta existencias en cada venta.</small>
						</div>
						<InputSwitch id="hasStock" v-model="product.hasStock" />
					</div>

					<div class="field toggle-row" v-if="isRestaurant && product.hasStock && !product.hasComposition">
						<div class="toggle-row__text">
							<label for="operatorSyncable">Permitir sincronización por operador</label>
							<small>Para productos con merma natural: el operador puede encerar su stock y queda registrado.</small>
						</div>
						<InputSwitch id="operatorSyncable" v-model="product.operatorSyncable" />
					</div>

					<!-- Uso: solo con inventario y solo en el giro de restaurante (ver isRestaurant). -->
					<!-- Un negocio de servicios o estándar no ve nada de esto. -->
					<template v-if="isRestaurant && product.hasStock && !product.hasComposition">
						<!-- Un solo selector: de «se vende» y «control de stock» solo tres combinaciones son válidas. -->
						<!-- Es el mismo del alta desde una compra (models/productUsage.js). -->
						<div class="field">
							<label for="productKind">Uso</label>
							<SelectButton id="productKind" v-model="productKind" :options="productKinds"
										optionLabel="label" optionValue="value" :unselectable="false"
										class="iva-select" aria-labelledby="productKind" />
							<small class="field-hint">{{ productKindHint }}</small>
						</div>

						<div class="field">
							<label for="parLevel">Nivel par (opcional)</label>
							<InputNumber id="parLevel" v-model="product.parLevel" mode="decimal" :min="0"
										:minFractionDigits="0" :maxFractionDigits="3" :useGrouping="false"
										locale="en-US" placeholder="Stock que quieres tener" />
							<small class="field-hint">La lista de compras sugiere lo que falta para llegar a esta cantidad.</small>
						</div>
					</template>

					<Divider align="left" class="section-divider">
						<span class="section-divider__label">Proveedor</span>
					</Divider>

					<div class="field">
						<label for="provider">Proveedor (Opcional)</label>
						<!-- showClear: la «X» deja el producto sin proveedor (H62). -->
						<Dropdown id="provider" v-model="product.provider" :options="this.providers" optionLabel="company" required="false" showClear autofocus />
					</div>

					<div class="field">
						<label for="category">Categoría (Opcional)</label>
						<Dropdown id="category" v-model="product.category" :options="this.categories" optionLabel="name" dataKey="id" showClear editable placeholder="Sin categoría" />
						<small style="color: #777;">Agrupa tus productos (Bebidas, Alimentos…). Escribe una nueva para crearla.</small>
					</div>

					<Divider align="left" class="section-divider">
						<span class="section-divider__label">Más</span>
					</Divider>

					<div class="field toggle-row">
						<div class="toggle-row__text">
							<label for="isPrintable">Imprimir al agregar</label>
							<small>Envía el ítem a la impresora al añadirlo a una venta.</small>
						</div>
						<InputSwitch id="isPrintable" v-model="product.isPrintable" />
					</div>

					<div class="field toggle-row" v-if="product.stockMode !== 'PERIODIC'">
						<div class="toggle-row__text">
							<label for="hasComposition">Habilitar composición</label>
							<small>Permite crear productos compuestos.</small>
						</div>
						<InputSwitch id="hasComposition" v-model="product.hasComposition" />
					</div>

					<!-- Factor Q: costo de condimentos que no se descuentan por receta -->
					<template v-if="product.hasComposition">
						<Divider align="left" class="section-divider">
							<span class="section-divider__label">Factor Q</span>
						</Divider>
						<div class="formgrid grid">
							<div class="field col-6">
								<label for="qFactorType">Tipo</label>
								<Dropdown id="qFactorType" v-model="product.qFactorType" :options="qFactorTypes"
										optionLabel="label" optionValue="value" placeholder="Sin factor Q" showClear />
							</div>
							<div class="field col-6" v-if="product.qFactorType">
								<label for="qFactorValue">{{ product.qFactorType === 'PERCENT' ? 'Porcentaje (%)' : 'Monto por unidad ($)' }}</label>
								<InputNumber id="qFactorValue" v-model="product.qFactorValue" mode="decimal" :min="0"
											:max="product.qFactorType === 'PERCENT' ? 99.99 : null"
											:minFractionDigits="0" :maxFractionDigits="4" :useGrouping="false" locale="en-US" />
							</div>
						</div>
						<small class="field-hint">Sal, aceite y especias que se controlan por conteo. Solo sirve para costear y fijar el precio: no cambia el costo de la venta.</small>
						<div v-if="recipeCostPreview" class="recipe-cost">
							<span>Componentes {{ recipeCostPreview.components }}</span>
							<span>+ Factor Q {{ recipeCostPreview.q }}</span>
							<strong>= Costo de receta {{ recipeCostPreview.total }}</strong>
							<span v-if="recipeCostPreview.margin !== null">· Margen {{ recipeCostPreview.margin }}%</span>
						</div>
					</template>
					<template #footer>
						<Button label="Cancelar" icon="pi pi-times" class="p-button-secondary mr-2 mb-2" @click="hideDialog"/>
						<Button label="Guardar" icon="pi pi-check" class="p-button-success mr-2 mb-2" @click="saveProduct" />
					</template>
				</Dialog>

				<Dialog v-model:visible="deleteProductDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
					<div class="flex align-items-center justify-content-center">
						<i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
						<span v-if="product">¿Está seguro de querer borrar <b>{{product.name}}</b>?</span>
					</div>
					<template #footer>
						<Button label="No" icon="pi pi-times" class="p-button-secondary mr-2 mb-2" @click="deleteProductDialog = false"/>
						<Button label="Sí" icon="pi pi-check" class="p-button-warning mr-2 mb-2" @click="deleteProduct" />
					</template>
				</Dialog>

				<Dialog v-model:visible="deleteProductsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
					<div class="flex align-items-center justify-content-center">
						<i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
						<span v-if="product">¿Está seguro de querer borrar los productos seleccionados?</span>
					</div>
					<template #footer>
						<Button label="No" icon="pi pi-times" class="p-button-secondary mr-2 mb-2" @click="deleteProductsDialog = false"/>
						<Button label="Sí" icon="pi pi-check" class="p-button-warning mr-2 mb-2" @click="deleteSelectedProducts" />
					</template>
				</Dialog>

				<Dialog v-model:visible="inventoryDialog" :style="{width: '650px'}" header="Gestión de Inventario" :modal="true" class="p-fluid">
					<!-- Información del producto -->
					<div class="field mb-4">
						<div style="background: #f8f9fa; padding: 15px; border-radius: 6px; border-left: 4px solid #2196F3;">
							<h5 style="margin: 0 0 5px 0; color: #2196F3;">{{ selectedProduct.name }}</h5>
							<small style="color: #6c757d;">Código: {{ selectedProduct.code || 'Sin código' }}</small>
						</div>
					</div>

					<!-- Stock actual -->
					<div class="field mb-4">
						<label style="font-weight: 600; margin-bottom: 10px; display: block;">Unidades en inventario</label>
						<div style="background: #e3f2fd; padding: 20px; border-radius: 6px; text-align: center;">
							<div style="font-size: 2.5rem; font-weight: bold; color: #1976d2;">
							{{ calculatedStock }}
							</div>
							<div style="color: #666; margin-top: 5px;">
								<span v-if="selectedProduct.hasStock">unidades disponibles</span>
								<span v-else>unidades aproximadas (basado en componentes)</span>
							</div>
						</div>
					</div>
					<div class="field mb-4">
						<div style="display: flex; align-items: center; gap: 10px;">
							<a href="javascript:void(0)" @click="toggleMovements" style="color: #2196F3; text-decoration: none; font-size: 0.95rem;">
								<i :class="showMovements ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" style="font-size: 0.8rem;"></i>
								Ver últimos 5 movimientos
							</a>
							<i 
								v-if="movementsExpandedOnce"
								class="pi pi-refresh" 
								@click="reloadMovements"
								style="font-size: 0.95rem; color: #2196F3; cursor: pointer;"
								:class="{ 'pi-spin': loadingMovements }"
								title="Recargar movimientos"
							></i>
						</div>
						<div v-if="showMovements" style="margin-top: 15px; padding: 15px; background: #f8f9fa; border-radius: 6px;">
							<div v-if="loadingMovements" style="text-align: center; padding: 10px;">
								<i class="pi pi-spin pi-spinner" style="font-size: 1.5rem; color: #2196F3;"></i>
								<p style="color: #6c757d; margin-top: 10px;">Cargando movimientos...</p>
							</div>
							<div v-else-if="movements.length === 0">
								<p style="color: #6c757d; font-size: 0.9rem; margin: 0;">No hay movimientos registrados</p>
							</div>
							<div v-else>
								<div v-for="(movement, index) in movements" :key="index" style="padding: 10px; border-bottom: 1px solid #e0e0e0; display: flex; justify-content: space-between; align-items: center;" :style="index === movements.length - 1 ? 'border-bottom: none;' : ''">
									<div style="flex: 1;">
										<div style="font-weight: 600; color: #333; font-size: 0.9rem;">{{ movement.type || 'Movimiento' }}</div>
										<div style="font-size: 0.85rem; color: #666; margin-top: 2px;">{{ formatMovementDate(movement.date) }}</div>
										<div v-if="movement.document" style="font-size: 0.85rem; margin-top: 2px;">
											<a v-if="movement.purchaseId" :href="'#/purchase?id=' + movement.purchaseId" target="_blank" style="color: #2196F3; text-decoration: underline;">{{ movement.document }}</a>
											<a v-else-if="movement.saleId" :href="'#/sale?id=' + movement.saleId" target="_blank" style="color: #2196F3; text-decoration: underline;">{{ movement.document }}</a>
											<span v-else style="color: #999;">{{ movement.document }}</span>
										</div>
									</div>
									<div style="text-align: right;">
										<div style="font-weight: 600; font-size: 0.95rem;" :style="{ color: movement.in > 0 ? '#22c55e' : '#ef4444' }">
											{{ movement.in > 0 ? '+' : '' }}{{ movement.in > 0 ? movement.in : -movement.out }}
										</div>
										<div v-if="!selectedProduct.hasComposition" style="font-size: 0.85rem; color: #666; margin-top: 2px;">Saldo: {{ movement.balance }}</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Sincronizar con el físico (queda la evidencia del ajuste) -->
					<div class="field mb-3" v-if="selectedProduct.hasStock && !selectedProduct.hasComposition && $can('stockSync')">
						<Button
							label="Sincronizar con el stock físico"
							icon="pi pi-sync"
							class="p-button-outlined"
							style="width: 100%;"
							@click="goToStockSync"
						/>
					</div>

					<!-- Ajuste de inventario -->
					<div class="field mb-4" v-if="selectedProduct.hasStock">
						<Button 
							label="Ajustar inventario" 
							icon="pi pi-pencil" 
							class="p-button-warning" 
							style="width: 100%;"
							@click="toggleAdjustment"
						/>
						
						<!-- Formulario de ajuste (expandible) -->
						<div v-if="showAdjustment" style="margin-top: 15px; padding: 15px; background: #fff3cd; border-radius: 6px; border: 1px solid #ffc107;">
							<div class="field mb-3">
								<label for="adjustmentType">Tipo de ajuste</label>
								<Dropdown 
									id="adjustmentType" 
									v-model="adjustmentType" 
									:options="adjustmentTypes" 
									optionLabel="label" 
									optionValue="value"
									placeholder="Seleccione tipo de ajuste"
								/>
							</div>
							<div class="field mb-3">
								<label for="adjustmentQuantity">Cantidad</label>
								<InputText 
									id="adjustmentQuantity" 
									type="number" 
									v-model.trim="adjustmentQuantity" 
									placeholder="Ingrese cantidad"
								/>
							</div>
							<div class="field mb-3">
								<label for="adjustmentReasonCode">Tipo de motivo</label>
								<Dropdown
									id="adjustmentReasonCode"
									v-model="adjustmentReasonCode"
									:options="adjustmentReasonOptions"
									optionLabel="label"
									optionValue="value"
								/>
							</div>
							<div class="field mb-0">
								<label for="adjustmentReason">Motivo</label>
								<Textarea 
									id="adjustmentReason" 
									v-model="adjustmentReason" 
									rows="2" 
									placeholder="Describa el motivo del ajuste"
								/>
							</div>
							<div style="margin-top: 15px; text-align: right;">
								<Button 
									label="Guardar ajuste" 
									icon="pi pi-check" 
									class="p-button-warning" 
									style="width: auto;"
									@click="saveInventoryAdjustment"
									:disabled="!adjustmentType || !adjustmentQuantity"
								/>
							</div>
						</div>
					</div>

					<!-- Productos de la composición -->
					<div class="field mb-0" v-if="selectedProduct.hasComposition">
						<div style="padding: 15px; background: #f8f9fa; border-radius: 6px;">
							<label style="font-weight: 600; display: block; margin-bottom: 10px;">Productos de la composición</label>
							<small style="color: #6c757d; display: block; margin-bottom: 15px;">
								Define los productos que componen este artículo y sus proporciones
							</small>
								
								<!-- Buscar y agregar producto -->
								<div style="margin-bottom: 15px;">
									<AutoComplete 
										v-model="selectedCompositionProduct"
										:suggestions="availableProducts"
										@complete="searchProductsForComposition($event)"
										@item-select="addProductToComposition"
										field="name"
										placeholder="Buscar producto para agregar..."
										style="width: 100%;"
										:dropdown="false"
									>
										<template #item="slotProps">
											<div>
												<strong>{{ slotProps.item.name }}</strong>
												<div style="font-size: 0.85rem; color: #666;">Código: {{ slotProps.item.code || 'Sin código' }}</div>
											</div>
										</template>
									</AutoComplete>
								</div>
								
								<!-- Tabla de productos en composición -->
								<div v-if="loadingCompositions" style="text-align: center; padding: 20px;">
									<i class="pi pi-spin pi-spinner" style="font-size: 1.5rem; color: #2196F3;"></i>
									<p style="color: #6c757d; margin-top: 10px;">Cargando composiciones...</p>
								</div>
								<div v-else-if="compositionProducts.length === 0" style="padding: 20px; text-align: center; background: #fff; border: 1px dashed #ccc; border-radius: 6px;">
									<i class="pi pi-inbox" style="font-size: 2rem; color: #999;"></i>
									<p style="color: #999; margin-top: 10px;">No hay productos en la composición</p>
									<small style="color: #999;">Busque y agregue productos arriba</small>
								</div>
								<DataTable 
									v-else
									:value="compositionProducts" 
									responsiveLayout="scroll"
									style="margin-bottom: 15px;"
								>
									<Column field="product.code" header="Código" style="width: 20%;">
										<template #body="slotProps">
											{{ slotProps.data.product.code || '-' }}
										</template>
									</Column>
									<Column field="product.name" header="Producto" style="width: 45%;">
										<template #body="slotProps">
											{{ slotProps.data.product.name }}
										</template>
									</Column>
									<Column field="coefficient" header="Coeficiente" style="width: 25%;">
										<template #body="slotProps">
											<InputText 
												v-model.number="slotProps.data.coefficient" 
												type="number" 
												step="0.01"
												min="0.01"
												style="width: 100%;"
											/>
										</template>
									</Column>
									<Column header="Acciones" style="width: 10%; text-align: center;">
										<template #body="slotProps">
											<Button 
												icon="pi pi-trash" 
												class="p-button-rounded p-button-danger p-button-text" 
												@click="removeProductFromComposition(slotProps.index)"
											/>
										</template>
									</Column>
								</DataTable>
								
								<!-- Botón guardar -->
								<div v-if="compositionProducts.length > 0" style="text-align: right;">
									<Button 
										label="Guardar composición" 
										icon="pi pi-save" 
										class="p-button-info" 
										style="width: auto;"
										@click="saveComposition"
									/>
								</div>
						</div>
					</div>

				<template #footer>
					<Button label="Cerrar" icon="pi pi-times" class="p-button-secondary" style="width: auto;" @click="closeInventoryDialog"/>
				</template>
			</Dialog>

			<Dialog v-model:visible="deleteCompositionsDialog" :style="{width: '450px'}" header="Confirmar eliminación" :modal="true">
				<div class="flex align-items-center justify-content-center">
					<i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: #ffc107;" />
					<span>Al deshabilitar las composiciones se eliminarán todas las composiciones existentes de este producto. ¿Desea continuar?</span>
				</div>
				<template #footer>
					<Button label="Cancelar" icon="pi pi-times" class="p-button-secondary mr-2 mb-2" @click="cancelDeleteCompositions"/>
					<Button label="Sí, eliminar" icon="pi pi-check" class="p-button-warning mr-2 mb-2" @click="confirmDeleteAllCompositions" />
				</template>
			</Dialog>
		</div>
		<div class="card">
			<DataTable ref="dt" :value="saleProducts" dataKey="id" :paginator="true" :rows="20" :filters="saleProductsFilters"
						paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" :rowsPerPageOptions="[20, 35, 50]"
						currentPageReportTemplate="Mostrando de {first} a {last} de {totalRecords} producto(s)" responsiveLayout="scroll">
				<template #header>
						<div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
							<h5 class="m-0">Reporte de ventas por Producto/Servicio</h5>
							<div class="table-header-inputs-container">
								<span class="block mt-4 md:mt-0 p-input-icon-left">
									<AutoComplete placeholder="Buscar" :dropdown="false" :multiple="false" v-model="saleProductsFilters['global'].value" style="margin-right: 20px;"></AutoComplete>
								</span>
								<span class="table-buttons-container block mt-4 md:mt-0">
									<Calendar :showIcon="true" v-model="startFilterDate" dateFormat="dd/mm/yy" placeholder="Fecha inicial" style="width: 200px; margin-right: 20px;"></Calendar>
									<Calendar :showIcon="true" v-model="endFilterDate" dateFormat="dd/mm/yy" placeholder="Fecha final" style="width: 200px; margin-right: 20px;"></Calendar>
									<Button icon="pi pi-search" class="p-button-rounded p-button-help mr-2" :loading="loadingProductSaleSearchButton" @click="getProductSalesReport" />
								</span>
							</div>
						</div>
					</template>

					<Column field="code" header="Código" :sortable="true" headerStyle="width:19%; min-width:8rem;">
						<template #body="slotProps">
							<span class="p-column-title">Código</span>
							{{slotProps.data.code}}
						</template>
					</Column>
					<Column field="name" header="Nombre" :sortable="true" headerStyle="width:19%; min-width:8rem;">
						<template #body="slotProps">
							<span class="p-column-title">Nombre</span>
							{{slotProps.data.name}}
						</template>
					</Column>
					<Column field="sold" header="Cant. Vendida" :sortable="true" headerStyle="width:19%; min-width:8rem;">
						<template #body="slotProps">
							<span class="p-column-title">Cant. Vendida</span>
							{{slotProps.data.sold}}
						</template>
					</Column>
					<Column field="averagePrice" header="Precio Unit. Promed." :sortable="true" headerStyle="width:19%; min-width:8rem;">
						<template #body="slotProps">
							<span class="p-column-title">Precio Unit. Promed.</span>
							{{slotProps.data.averagePrice.toFixed(2)}}
						</template>
					</Column>
					<Column field="total" header="Total Vendido" :sortable="true" headerStyle="width:19%; min-width:8rem;">
						<template #body="slotProps">
							<span class="p-column-title">Total Vendido</span>
							{{slotProps.data.total.toFixed(2)}}
						</template>
					</Column>

				</DataTable>			
			</div>
		</div>
	</div>

</template>

<script>
import { saveDownload } from '../../service/fileDownload';
import { deleteOutcome } from '../../service/deleteOutcome';
import {FilterMatchMode} from 'primevue/api';
import httpRequest from '../../service/httpRequest';
import { normalizeProductSalesReport, reportRange } from './productSalesReport';
import Product from '../../models/product';
import ProductProvider from '../../models/productProvider';
import Big from 'big.js';
import { reasonsFor } from './Inventory/inventoryFormat';
import { PRODUCT_KINDS, usageFor, kindFor, kindHint } from '../../models/productUsage';
import { percentToFraction, fractionToPercent, formatPercent } from '../../service/discountPercent';

export default {
	data() {
		return {
			providers: [],
			categories: [],
			products: [],
			saleProducts: [],
			productDialog: false,
			deleteProductDialog: false,
			deleteProductsDialog: false,
			inventoryDialog: false,
			product: {},
			// El descuento se edita en % y se guarda como fracción (H17).
			discountPercent: 0,
			// Tarifas de IVA ofrecidas al usuario. Son los codigoPorcentaje del
			// SRI (ver TaxCatalog en el backend). Solo se ofrecen las VIGENTES:
			// 12% y 14% existen en el catalogo pero son historicas.
			ivaOptions: [
				{ label: '0%',  value: '0' },
				{ label: '5%',  value: '5' },
				{ label: '15%', value: '4' }
			],
			iceTypes: [
				{ label: 'Ad valorem', value: 'AD_VALOREM' },
				{ label: 'Específica', value: 'SPECIFIC' },
				{ label: 'Mixta',      value: 'MIXED' }
			],
			selectedProduct: {},
			selectedProducts: null,
			filters: {},
			saleProductsFilters: {},
			submitted: false,
			loadingDownloadButton: false,
			isAuthorized: true,
			startFilterDate: null,
			endFilterDate: null,
			loadingProductSaleSearchButton: false,
			// Inventory dialog controls
			showMovements: false,
			showAdjustment: false,
			adjustmentType: null,
			adjustmentQuantity: null,
			adjustmentReason: '',
			adjustmentReasonCode: 'OTRO',
			productKinds: PRODUCT_KINDS,
			qFactorTypes: [
				{ label: 'Porcentaje', value: 'PERCENT' },
				{ label: 'Monto fijo', value: 'AMOUNT' }
			],
			adjustmentTypes: [
				{ label: 'Entrada (+)', value: 'entrada' },
				{ label: 'Salida (-)', value: 'salida' }
			],
			movements: [],
			loadingMovements: false,
			movementsExpandedOnce: false,
			// Composition controls
			compositionProducts: [],
			availableProducts: [],
			selectedCompositionProduct: null,
			loadingCompositions: false,
			deleteCompositionsDialog: false,
		}
	},
	async created() {
        this.initFilters();
        this.getProducts();
		this.isAuthorized = this.$can('productsManage');
	},
	mounted() {
    },
	methods: {
		async onFilterChanged(event) {
			let filter = event.query;
            if (filter.length > 1) {
                const result = await httpRequest.getProducts(filter);
                if (result.status === 200) {
                    this.products = result.data.map(product => new Product(product));
                } else { this.products = [] }
            }
		},
		// Etiqueta de tarifa para la tabla. Cubre tambien codigos historicos
		// (12/14%) que ya no se ofrecen pero pueden existir en datos viejos.
		formatPercent,
		ivaLabel(product) {
			const rates = { '0': '0%', '5': '5%', '8': '8%', '2': '12%', '3': '14%', '4': '15%' };
			return rates[product.ivaCode] || (product.hasTax ? '15%' : '0%');
		},
		async openNew() {
			await this.getProviders();
			await this.getCategories();
			this.product = new Product();
			this.discountPercent = 0;
			this.submitted = false;
			this.productDialog = true;
		},
		hideDialog() {
			this.selectedProducts = null;
			this.productDialog = false;
			this.submitted = false;
		},
		async saveProduct() {
			this.selectedProducts = null;
			this.product.discount = percentToFraction(this.discountPercent);
            let response = await httpRequest.saveProduct(this.product);
            if (response.status == 200) {
                let product = new Product(response.data);
                this.addProductToList(product);
                // Si el usuario escribio una categoria nueva, el backend la creo:
                // recargamos para que aparezca en el desplegable la proxima vez.
                await this.getCategories();
            } else if (response.status == 422) {
                // Configuracion invalida (p. ej. un insumo de una receta pasado a
                // conteo periodico): el dialogo queda abierto para corregirla.
                this.$toast.add({severity:'error', summary: 'No se guardó', detail: response.error, life: 6000});
                return;
            } else { console.log("Error: ", response.status); }
            this.hideDialog();
		},
        async getProducts() {
            let response = await httpRequest.getProducts();
            if (response.status == 200) {
                let products = response.data;
                this.products = products.map(function(data) { return new Product(data)});
            } else { console.log("Error: ", response.status); }
        },
		async editProduct(product) {
			await this.getProviders();
			await this.getCategories();
			this.product = {...product};
			this.discountPercent = fractionToPercent(product.discount);
			this.productDialog = true;
		},
		confirmDeleteProduct(product) {
			this.selectedProducts = null;
			this.product = product;
			this.deleteProductDialog = true;
		},
		async deleteProduct() {
			this.selectedProducts = null;
            const ids = [this.product.id];
            const response = await httpRequest.deleteProducts(ids);
            // Un producto con stock no se borra: el backend dice por qué y la fila se queda.
            const outcome = deleteOutcome(response, ids, {
                success: 'Producto eliminado', failure: 'Hemos tenido un problema borrando el producto'});
            this.removeProducts(outcome.deletedIds);
            this.$toast.add(outcome.toast);
			this.deleteProductDialog = false;
		},
		async downloadProducts() {
			this.loadingDownloadButton = true;
			try {
				const response = await httpRequest.downloadProducts();
				await saveDownload(response, "productos" + ".xlsx", "application/vnd.ms-excel", this.$toast);
			} finally {
				this.loadingDownloadButton = false;
			}
		},
		confirmDeleteSelected() {
			this.deleteProductsDialog = true;
		},
		async deleteSelectedProducts() {
            const ids = this.selectedProducts.map(product => product.id);
            const response = await httpRequest.deleteProducts(ids);
            const outcome = deleteOutcome(response, ids, {
                success: 'Producto eliminado', failure: 'Hemos tenido un problema borrando los productos seleccionados'});
            this.removeProducts(outcome.deletedIds);
            this.$toast.add(outcome.toast);
            this.selectedProducts = null;
			this.deleteProductsDialog = false;
        },
		async getProductSalesReport() {
			this.loadingProductSaleSearchButton = true;
			// Días completos, en hora local (H64): la fecha final incluye su día entero.
			const range = reportRange(this.startFilterDate, this.endFilterDate);
			let response = await httpRequest.getProductSalesReport(range.initialDate, range.finalDate);
			this.loadingProductSaleSearchButton = false;
			if (response.status) {
				// total y sold llegan como texto: se pasan a número para mostrarlos y ordenarlos (H28).
				this.saleProducts = normalizeProductSalesReport(response.data);
            } else {
                this.$toast.add({severity:'error', summary: 'Error', detail:'Hemos tenido un problema obteniendo el reporte, intente nuevamente.', life: 3000});
            }
		},
		initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
            }
			this.saleProductsFilters = {
				'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
			}
        },
        productIndex(product) {
            var index = -1;
            if (product.id === null) { index = -1; return index; }
            for (var i=0; i<this.products.length; i++) {
                if (this.products[i].id === product.id) { return i; }
            }
            return index;
        },
        addProductToList(product) {
            let index = this.productIndex(product);
            if (index > -1) { this.products[index] = product }
            else { this.products = [product, ...this.products] } 
        },
        // Quita de la lista solo los que el backend borró (uno con stock se queda).
        removeProducts(ids) {
            const gone = new Set(ids);
            this.products = this.products.filter(product => !gone.has(product.id));
            if (this.product && gone.has(this.product.id)) { this.product = null; }
        },
		async getProviders() {
            let response = await httpRequest.getProviders(true);
            if (response.status == 200) {
				this.providers = response.data.map(provider => { return new ProductProvider(provider) })
            }
        },
		async getCategories() {
            let response = await httpRequest.getProductCategories();
            if (response.status == 200) { this.categories = response.data }
        },
		async openInventoryDialog(product) {
			this.selectedProduct = {...product};
			this.showMovements = false;
			this.showAdjustment = false;
			this.adjustmentType = null;
			this.adjustmentQuantity = null;
			this.adjustmentReason = '';
			this.adjustmentReasonCode = product.stockMode === 'PERIODIC' ? 'CONSUMO' : 'OTRO';
			this.movements = [];
			this.compositionProducts = [];
			this.selectedCompositionProduct = null;
			this.inventoryDialog = true;
			
			// Cargar los últimos 5 movimientos del kardex
			this.loadingMovements = true;
			try {
				const response = await httpRequest.getProductKardex(product.id, null, null, 5);
				if (response.status === 200 && response.data) {
					this.movements = response.data;
				}
			} catch (error) {
				console.error('Error al cargar movimientos:', error);
			} finally {
				this.loadingMovements = false;
			}
			
			// Cargar composiciones si existen
			if (product.hasComposition) {
				await this.loadCompositions();
			}
		},
		closeInventoryDialog() {
			this.inventoryDialog = false;
			this.showMovements = false;
			this.showAdjustment = false;
			this.adjustmentType = null;
			this.adjustmentQuantity = null;
			this.adjustmentReason = '';
			this.selectedProduct = {};
			this.movementsExpandedOnce = false;
			this.compositionProducts = [];
			this.selectedCompositionProduct = null;
		},
		toggleMovements() {
			this.showMovements = !this.showMovements;
			if (this.showMovements) {
				this.movementsExpandedOnce = true;
			}
		},
		async reloadMovements() {
			if (!this.selectedProduct.id) return;
			
			this.loadingMovements = true;
			try {
				const response = await httpRequest.getProductKardex(this.selectedProduct.id, null, null, 5);
				if (response.status === 200 && response.data) {
					this.movements = response.data;
				}
			} catch (error) {
				console.error('Error al recargar movimientos:', error);
				this.$toast.add({severity:'error', summary: 'Error', detail: 'No se pudieron recargar los movimientos', life: 3000});
			} finally {
				this.loadingMovements = false;
			}
		},
		toggleAdjustment() {
			this.showAdjustment = !this.showAdjustment;
			if (!this.showAdjustment) {
				this.adjustmentType = null;
				this.adjustmentQuantity = null;
				this.adjustmentReason = '';
			}
		},
		async saveInventoryAdjustment() {
			if (!this.adjustmentType || !this.adjustmentQuantity) {
				this.$toast.add({severity:'warn', summary: 'Advertencia', detail: 'Complete todos los campos requeridos', life: 3000});
				return;
			}

			// Llamar al endpoint de ajuste de inventario
			let response = await httpRequest.saveProductStockAdjustment(
				this.selectedProduct.id,
				this.adjustmentType,
				parseFloat(this.adjustmentQuantity),
				this.adjustmentReason || '',
				this.adjustmentReasonCode
			);
			
			if (response.status == 200) {
				// Actualizar el stock del producto en la lista
				let product = new Product(response.data);
				this.addProductToList(product);
				this.selectedProduct.stock = product.stock;
				
				this.$toast.add({severity:'success', summary: 'Éxito', detail: 'Ajuste de inventario guardado correctamente', life: 3000});
				
				// Recargar los movimientos para mostrar el nuevo ajuste
				await this.reloadMovements();
				
				// Resetear el formulario de ajuste
				this.showAdjustment = false;
				this.adjustmentType = null;
				this.adjustmentQuantity = null;
				this.adjustmentReason = '';
				this.adjustmentReasonCode = this.selectedProduct.stockMode === 'PERIODIC' ? 'CONSUMO' : 'OTRO';
			} else {
				this.$toast.add({severity:'error', summary: 'Error', detail: response.error || 'Error al guardar el ajuste', life: 3000});
			}
		},
		goToStockSync() {
			this.inventoryDialog = false;
			this.$router.push({ path: '/dashboard/inventario/sincronizar', query: { productId: this.selectedProduct.id } });
		},
		async saveComposition() {
			if (this.compositionProducts.length === 0) {
				this.$toast.add({severity:'warn', summary: 'Advertencia', detail: 'Debe agregar al menos un producto a la composición', life: 3000});
				return;
			}

			// Luego guardar las composiciones
			const compositions = this.compositionProducts.map(cp => ({
				...(cp.id && { id: cp.id }),
				baseProductId: cp.product.id,
				coefficient: parseFloat(cp.coefficient)
			}));

			let response = await httpRequest.saveProductCompositions(this.selectedProduct.id, compositions);
			if (response.status == 200) {
				// Actualizar compositionProducts con los datos retornados (incluye los IDs asignados)
				this.compositionProducts = response.data.map(comp => ({
					id: comp.id,
					product: new Product(comp.baseProduct),
					coefficient: comp.coefficient
				}));
				
				// Actualizar hasComposition localmente
				this.selectedProduct.hasComposition = true;
				
				// Actualizar el producto en la lista principal
				const productToUpdate = this.products.find(p => p.id === this.selectedProduct.id);
				if (productToUpdate) {
					productToUpdate.hasComposition = true;
				}
				
				this.$toast.add({severity:'success', summary: 'Éxito', detail: 'Composición guardada correctamente', life: 3000});
			} else {
				this.$toast.add({severity:'error', summary: 'Error', detail: response.error || 'Error al guardar la composición', life: 6000});
			}
		},
		formatMovementDate(date) {
			if (!date) return '-';
			try {
				const d = new Date(date);
				return d.toLocaleDateString('es-ES', { 
					day: '2-digit', 
					month: '2-digit', 
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit'
				});
			} catch (e) {
				return date;
			}
		},
		async searchProductsForComposition(event) {
			let filter = event.query;
			if (filter.length > 1) {
				const result = await httpRequest.getProducts(filter);
				if (result.status === 200) {
					// Filtrar el producto actual y los ya agregados
					const addedIds = this.compositionProducts.map(cp => cp.product.id);
					this.availableProducts = result.data
						.map(product => new Product(product))
						.filter(p => p.id !== this.selectedProduct.id && !addedIds.includes(p.id))
						// Un insumo de conteo periódico no entra en recetas: va en el factor Q.
						.filter(p => p.stockMode !== 'PERIODIC');
				} else {
					this.availableProducts = [];
				}
			}
		},
		addProductToComposition() {
			if (!this.selectedCompositionProduct) {
				return;
			}

			// Verificar que no esté ya agregado
			const exists = this.compositionProducts.some(cp => cp.product.id === this.selectedCompositionProduct.id);
			if (exists) {
				this.$toast.add({severity:'warn', summary: 'Advertencia', detail: 'El producto ya está en la composición', life: 3000});
				this.selectedCompositionProduct = null;
				return;
			}

			this.compositionProducts.push({
				product: this.selectedCompositionProduct,
				coefficient: 1
			});

			this.selectedCompositionProduct = null;
		},
		async removeProductFromComposition(index) {
			const composition = this.compositionProducts[index];
			
			// Remover del array local inmediatamente para mantener el comportamiento actual
			this.compositionProducts.splice(index, 1);
			
			// Si la lista queda vacía, actualizar hasComposition localmente
			if (this.compositionProducts.length === 0) {
				this.selectedProduct.hasComposition = false;
			}
			
			// Si la composición tiene ID (ya fue guardada), llamar al endpoint de eliminación
			if (composition.id) {
				try {
					const response = await httpRequest.deleteProductComposition(composition.id);
					if (response.status === 200) {
						this.$toast.add({severity:'success', summary: 'Éxito', detail: 'Composición eliminada', life: 3000});
					} else {
						this.$toast.add({severity:'error', summary: 'Error', detail: 'Error al eliminar la composición del servidor', life: 3000});
					}
				} catch (error) {
					console.error('Error al eliminar composición:', error);
					this.$toast.add({severity:'error', summary: 'Error', detail: 'Error al eliminar la composición', life: 3000});
				}
			}
		},
		async loadCompositions() {
			this.loadingCompositions = true;
			try {
				const response = await httpRequest.getProductCompositions(this.selectedProduct.id);
				if (response.status === 200 && response.data) {
					this.compositionProducts = response.data.map(comp => ({
						id: comp.id,
						product: new Product(comp.baseProduct),
						coefficient: comp.coefficient
					}));
				}
			} catch (error) {
				console.error('Error al cargar composiciones:', error);
			} finally {
				this.loadingCompositions = false;
			}
		},
		onCompositionToggle(value) {
			if (value) {
				// Actualizar el estado
				this.selectedProduct.hasComposition = true;
				// Esperar a que el DOM se actualice
				this.$nextTick(() => {
					if (this.$refs.compositionSection) {
						this.$refs.compositionSection.scrollIntoView({ 
							behavior: 'smooth', 
							block: 'nearest' 
						});
					}
				});
			} else {
				// Si está deshabilitando y hay composiciones guardadas, pedir confirmación
				if (this.compositionProducts.length > 0 && this.compositionProducts.some(cp => cp.id)) {
					// Revertir el switch temporalmente
					this.selectedProduct.hasComposition = true;
					// Cerrar el dialog de inventario
					this.inventoryDialog = false;
					// Esperar a que se cierre y luego mostrar el diálogo de confirmación
					this.$nextTick(() => {
						this.deleteCompositionsDialog = true;
					});
				} else {
					// No hay composiciones guardadas, simplemente deshabilitar
					this.selectedProduct.hasComposition = false;
				}
			}
		},
		async confirmDeleteAllCompositions() {
			const compositionsToDelete = this.compositionProducts.filter(cp => cp.id);
			
			if (compositionsToDelete.length === 0) {
				// No hay composiciones guardadas, solo limpiar localmente
				this.compositionProducts = [];
				this.selectedProduct.hasComposition = false;
				this.deleteCompositionsDialog = false;
				// Reabrir el dialog de inventario
				this.$nextTick(() => {
					this.inventoryDialog = true;
				});
				return;
			}

			// Eliminar todas las composiciones del backend
			let errors = 0;
			for (const comp of compositionsToDelete) {
				try {
					const response = await httpRequest.deleteProductComposition(comp.id);
					if (response.status !== 200) {
						errors++;
					}
				} catch (error) {
					console.error('Error al eliminar composición:', error);
					errors++;
				}
			}

			if (errors > 0) {
				this.$toast.add({severity:'error', summary: 'Error', detail: 'Hubo problemas eliminando algunas composiciones', life: 3000});
				// Revertir el cambio
				this.selectedProduct.hasComposition = true;
			} else {
				// Limpiar localmente
				this.compositionProducts = [];
				this.selectedProduct.hasComposition = false;
				
				// Actualizar el producto en la lista principal
				const productToUpdate = this.products.find(p => p.id === this.selectedProduct.id);
				if (productToUpdate) {
					productToUpdate.hasComposition = false;
				}
				
				this.$toast.add({severity:'success', summary: 'Éxito', detail: 'Composiciones eliminadas correctamente', life: 3000});
			}

			this.deleteCompositionsDialog = false;
			// Reabrir el dialog de inventario
			this.$nextTick(() => {
				this.inventoryDialog = true;
			});
		},
		cancelDeleteCompositions() {
			// Mantener el switch activado
			this.selectedProduct.hasComposition = true;
			this.deleteCompositionsDialog = false;
			// Reabrir el dialog de inventario
			this.$nextTick(() => {
				this.inventoryDialog = true;
			});
		},
	},
	computed: {
		// El merchant no tiene por que saber que tarifa aplica a que producto:
		// el hint lo dice en el momento de elegir.
		ivaHint() {
			switch (this.product.ivaCode) {
				case '0': return 'No genera IVA.';
				case '5': return 'Materiales de construcción (Ley 2024).';
				case '4': return 'Tarifa general vigente.';
				case '2': return 'Tarifa histórica (12%). Elige una vigente para vender hoy.';
				case '3': return 'Tarifa histórica (14%). Elige una vigente para vender hoy.';
				default:  return 'Selecciona la tarifa que aplica a este producto.';
			}
		},
		// Sincronización por operador, control de stock, «Se vende» y nivel par son
		// configuraciones del giro de restaurante. Hasta que exista una pantalla propia
		// del giro, solo se muestran cuando el negocio es 'restaurant' (el mismo valor
		// que usa saleForm.vue). Los demás giros guardan los valores por defecto del modelo.
		isRestaurant() {
			const business = this.$store.getters.business;
			return !!business && business.businessType === 'restaurant';
		},
		// Uso del producto: se lee de `isSellable` + `stockMode` y se escribe en los dos.
		productKind: {
			get() { return kindFor(this.product); },
			set(kind) {
				const usage = usageFor(kind);
				this.product.isSellable = usage.isSellable;
				this.product.stockMode = usage.stockMode;
			},
		},
		productKindHint() {
			return kindHint(this.productKind);
		},
		adjustmentReasonOptions() {
			return reasonsFor(this.selectedProduct.stockMode === 'PERIODIC');
		},
		// Costo de receta en la ficha: componentes (FIFO) + factor Q. El backend
		// envia currentCost solo a administradores; sin el, no hay desglose.
		recipeCostPreview() {
			const p = this.product;
			if (!p || !p.hasComposition || p.currentCost === null || p.currentCost === undefined) return null;
			const base = Number(p.currentCost);
			const value = Number(p.qFactorValue) || 0;
			const q = !p.qFactorType ? 0 : (p.qFactorType === 'PERCENT' ? base * value / 100 : value);
			const total = base + q;
			const price = Number(p.price) || 0;
			const fmt = n => '$' + n.toFixed(4);
			return {
				components: fmt(base),
				q: fmt(q),
				total: fmt(total),
				margin: price > 0 ? ((price - total) / price * 100).toFixed(1) : null,
			};
		},
		showIceAdValorem() {
			return ['AD_VALOREM', 'MIXED'].includes(this.product.iceType);
		},
		showIceSpecific() {
			return ['SPECIFIC', 'MIXED'].includes(this.product.iceType);
		},
		calculatedStock() {
			// Si hasStock es true, mostrar el stock directo
			if (this.selectedProduct.hasStock) {
				return this.selectedProduct.stock || 0;
			}
			
			// Si hasStock es false, calcular basado en composiciones
			if (this.compositionProducts.length === 0) {
				return 0;
			}

			// Calcular el stock aproximado basado en los productos base
			const possibleUnits = this.compositionProducts.map(comp => {
				const baseStock = comp.product.stock || 0;
				const coefficient = parseFloat(comp.coefficient) || 1;
				return Math.floor(baseStock / coefficient);
			});

			// Retornar el mínimo (cantidad máxima que podemos producir)
			return possibleUnits.length > 0 ? Math.min(...possibleUnits) : 0;
		}
	},
	watch: {
		'product.stockMode'(newValue) {
			// Un periodico no se vende ni va en recetas.
			if (newValue === 'PERIODIC') {
				this.product.isSellable = false;
				this.product.hasComposition = false;
			}
		},
		'product.hasComposition'(newValue) {
			if (newValue === false && this.product) {
				this.product.qFactorType = null;
				this.product.qFactorValue = null;
			}
		},
		'product.qFactorType'(newValue) {
			if (!newValue && this.product) { this.product.qFactorValue = null; }
		},
		// `ivaCode` es la fuente de verdad; `hasTax` queda derivado para que la
		// tabla y el resto de la app sigan coherentes sin esperar al backend.
		'product.ivaCode'(newValue) {
			this.product.hasTax = newValue !== '0';
		},
		'product.hasIce'(newValue) {
			if (newValue === false) {
				this.product.iceCode = null;
				this.product.iceType = null;
				this.product.iceRate = null;
				this.product.iceSpecificRate = null;
			}
		},
		'product.hasStock'(newValue) {
			if (newValue === false) {
				this.product.stock = null;
				// Sin inventario no hay insumo: se vende y es perpetuo (igual que el backend).
				this.product.stockMode = 'PERPETUAL';
				this.product.isSellable = true;
				this.product.parLevel = null;
			} else if (newValue === true && (this.product.stock === null || this.product.stock === undefined)) {
				this.product.stock = Big(0);
			}
		}
	}
}
</script>

<style scoped lang="scss">
@import '../../assets/demo/badges.scss';

/* ---------------------------------------------------------------------------
   Diálogo de producto — sección de impuestos
   Se apoya solo en los tokens del tema (var(--...)) para que herede cualquier
   cambio de tema de PrimeVue en vez de fijar colores propios.
--------------------------------------------------------------------------- */

/* Acciones de fila: SIEMPRE en una sola línea. Antes eran botones inline con
   utilidades de margen (mr-2 / mt-2) y, con los 3 botones, el de borrar se
   envolvía a una segunda línea (el mt-2 era el parche de ese salto). */
.row-actions {
	display: flex;
	flex-wrap: nowrap;
	align-items: center;
	gap: 0.5rem;

	:deep(.p-button) {
		flex: 0 0 auto;
	}
}

/* Marca de insumo en la tabla: no se vende. */
.supply-flag {
	display: inline-block;
	margin-left: 0.5rem;
	padding: 0 0.4rem;
	border-radius: 4px;
	font-size: 0.7rem;
	font-weight: 600;
	letter-spacing: 0.03em;
	color: var(--text-color-secondary);
	background: var(--surface-200);
	vertical-align: middle;
}

/* Desglose del costo de receta (factor Q). */
.recipe-cost {
	display: flex;
	flex-wrap: wrap;
	gap: 0.35rem 0.6rem;
	margin-top: 0.75rem;
	padding: 0.6rem 0.75rem;
	border-radius: 6px;
	background: var(--surface-100);
	font-size: 0.85rem;
	font-variant-numeric: tabular-nums;
}

/* Encabezado de sección: agrupa campos sin el peso de un Fieldset. */
.section-divider {
	margin: 1.75rem 0 1rem;

	&__label {
		font-size: 0.75rem;
		font-weight: 600;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--text-color-secondary);
	}
}

/* Tarifa de IVA: control segmentado a lo ancho, segmentos iguales. Ver todas
   las tarifas de golpe es el punto — un dropdown escondería que existe el 5%. */
.iva-select {
	display: flex;

	:deep(.p-button) {
		flex: 1 1 0;
		justify-content: center;
		font-variant-numeric: tabular-nums;
	}
}

/* Texto de apoyo bajo un campo. Explica la consecuencia de la elección. */
.field-hint {
	display: block;
	margin-top: 0.5rem;
	color: var(--text-color-secondary);
	line-height: 1.35;
}

/* Fila etiqueta + switch. Reemplaza los divs espaciadores con estilos inline. */
.toggle-row {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;

	/* El switch NO debe encogerse: es un flex item de ancho fijo y con la
	descripción a dos líneas flexbox lo comprimía hasta recortarlo. */
	:deep(.p-inputswitch) {
		flex: 0 0 auto;
	}

	&__text {
		min-width: 0;

		label {
			display: block;
			margin-bottom: 0.15rem;
		}

		small {
			display: block;
			color: var(--text-color-secondary);
			line-height: 1.35;
		}
	}
}

/* Campos de ICE: aparecen solo al activarlo. La barra lateral los ata
   visualmente al switch que los revela. */
.ice-panel {
	margin: 0.25rem 0 0.5rem;
	padding: 1rem 0 0.25rem 1rem;
	border-left: 2px solid var(--surface-border);

	.field:last-child {
		margin-bottom: 0;
	}
}

/* Celda de IVA en la tabla: la tarifa se lee de un vistazo; 0% se atenúa
   para que las tarifas que sí generan impuesto salten a la vista. */
.iva-cell {
	font-variant-numeric: tabular-nums;

	&--zero {
		color: var(--text-color-secondary);
	}
}

.ice-flag {
	margin-left: 0.4rem;
	padding: 0.1rem 0.35rem;
	border: 1px solid var(--surface-border);
	border-radius: var(--border-radius);
	font-size: 0.65rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: var(--text-color-secondary);
}

@media screen and (max-width: 769px) {
	.table-header-inputs-container {
		display: block;
	}
	#table-search-input {
		margin: 15px 0px !important;
	}
}
@media screen and (min-width: 769px) {
	.table-header-inputs-container {
		display: flex;
	}
	#table-search-input {
		margin: 0px 15px;
	}
}
</style>
